import { sendGTMEvent } from "@next/third-parties/google";
import isNil from "lodash/isNil";
import { type GTMConfig } from "@/staticClientConfig";
import { type SiteAnalyticsType } from "@/types/site-analytics";
import { type SubBrand } from "@/types/site-options";

class SiteAnalyticsService {
  private readonly data: SiteAnalyticsType;
  private readonly gtmConfig: GTMConfig;

  constructor(data: SiteAnalyticsType, gtmConfig: GTMConfig) {
    this.data = data;
    this.gtmConfig = gtmConfig;
  }

  static resetDataLayer() {
    if (window.dataLayer) {
      window.dataLayer = [];
    }
  }

  public setInitialDataLayer() {
    window.dataLayer?.push(...this.getInitialDataLayer());
  }

  public getInitialDataLayer() {
    const {
      site: {
        title,
        eventId,
        eventCode,
        daysToEventEnd,
        l2Topics,
        l1Topics,
        mainL2Topic,
        googleAnalyticsId,
      },
      siteConfig: { sitePath, tenant },
      subBrands,
      analytics: { region, sector, sectorCode, vertical, eventEdition },
    } = this.data;

    return [
      this.getEnvironmentName(),
      this.getEventName(title),
      this.getEventId(eventId),
      this.getEventCode(eventCode),
      this.getEventDaysToEventEnd(daysToEventEnd),
      this.getL1Topics(l1Topics),
      this.getL2Topics(l2Topics),
      this.getMainTrackingTopic(mainL2Topic),
      this.getKnnEvent(sitePath, tenant),
      this.getSubBrands(subBrands),
      this.getGoogleAnalyticsId(googleAnalyticsId),
      this.getAnalyticsRegion(region),
      this.getAnalyticsEventEdition(eventEdition),
      this.getAnalyticsVertical(vertical),
      this.getAnalyticsSector(sector),
      this.getAnalyticsSectorCode(sectorCode),
      this.getDivision(),
    ].filter((item) => !isNil(item));
  }

  private getDivisionId(divisionId: string) {
    if (divisionId) {
      return { "division.id": divisionId };
    }
  }

  private getDivisionName(divisionName: string) {
    if (divisionName) {
      return { "division.name": divisionName };
    }
  }

  private setDivisionInfrontBusinessUnit(infrontBusinessUnitId: string) {
    if (infrontBusinessUnitId) {
      return {
        "division.infrontBusinessUnit": infrontBusinessUnitId,
      };
    }
  }

  private getEventName(eventName: string) {
    if (eventName) {
      return { "knnevent.name": eventName };
    }
  }

  private getEventId(eventId: string) {
    if (eventId) {
      return { "knnevent.eventId": eventId };
    }
  }

  private getEventCode(eventCode: string) {
    if (eventCode) {
      return { "knnevent.eventCode": eventCode };
    }
  }

  private getEventDaysToEventEnd(daysToEventEnd: string) {
    if (daysToEventEnd) {
      const endDate = daysToEventEnd === "null" ? "0" : daysToEventEnd;
      return { "knnevent.daysToEventEnd": endDate };
    }
  }

  private getKnnEvent(sitePath: string, tenant: string) {
    return {
      knnevent: {
        path: sitePath,
        domain: tenant.toLowerCase(),
      },
    };
  }

  private getL1Topics(l1Topics: string[]) {
    return { l1Topics };
  }

  private getL2Topics(l2Topics: string[]) {
    return { l2Topics };
  }

  private getMainTrackingTopic(mainTrackingTopic: string) {
    if (mainTrackingTopic) {
      return { mainTrackingTopic: mainTrackingTopic };
    }
  }

  private getSubBrands(subBrands: SubBrand[]) {
    return { brand: subBrands };
  }

  private getGoogleAnalyticsId(googleAnalyticsId: string) {
    if (googleAnalyticsId) {
      return { "knnevent.analytics": googleAnalyticsId };
    }
  }

  private getAnalyticsRegion(analyticsRegion?: string) {
    if (analyticsRegion) {
      return { analyticsRegion: analyticsRegion };
    }
  }

  private getAnalyticsEventEdition(analyticsEventEdition?: string) {
    if (analyticsEventEdition) {
      return { analyticsEventEdition: analyticsEventEdition };
    }
  }

  private getAnalyticsVertical(analyticsVertical?: string) {
    if (analyticsVertical) {
      return { analyticsVertical: analyticsVertical };
    }
  }

  private getAnalyticsSector(analyticsSector?: string) {
    if (analyticsSector) {
      return { analyticsSector: analyticsSector };
    }
  }

  private getAnalyticsSectorCode(analyticsSectorCode?: string) {
    if (analyticsSectorCode) {
      return { analyticsSectorCode: analyticsSectorCode };
    }
  }

  private getDivision() {
    const { division } = this.data;

    return {
      ...this.getDivisionId(division.id),
      ...this.getDivisionName(division.name),
      ...this.setDivisionInfrontBusinessUnit(division.infrontBusinessUnitId),
    };
  }

  private getEnvironmentName() {
    return {
      "environment.name": this.gtmConfig.environmentName,
    };
  }

  private pushToDataLayer(data: object) {
    sendGTMEvent(data);
  }
}

export default SiteAnalyticsService;
